import { useEffect, useMemo, useState } from 'react'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import {
  ConfirmDeletionModal,
  EmptyState,
  ImportantMessage,
  Mtds,
  PlusIcon,
  ScreenHeader,
  TopBar,
  AppLayout,
  useDeleteImportantMessageMutation,
  useErrorToast,
  useGetImportantMessagesQuery
} from 'prove-common'
import {
  ImportantMessagesScreenToggleGroup,
  ImportantMessagesToggleValue
} from '../components/important-messages-screen-toggle-group/ImportantMessagesScreenToggleGroup'
import { useSearchParams } from 'react-router'
import { ImportantMessageCard } from '../components/important-message-card/ImportantMessageCard'
import { CreateImportantMessageModal } from '../components/create-important-message-modal/CreateImportantMessageModal'
import { EditImportantMessageModal } from '../components/edit-important-message-modal/EditImportantMessageModal'
import { AppLogo } from '../components/app-logo/AppLogo'
import { PROVETA_URL } from '../constants'

// Custom hook to manage toggle value
const useToggleValue = (
  key: string,
  defaultValue: ImportantMessagesToggleValue
) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const toggleValueParam = searchParams.get(
    'toggle'
  ) as ImportantMessagesToggleValue | null
  // Initialize toggleValue from URL param or fallback to localStorage or 'all'
  const [toggleValue, setToggleValue] = useState<ImportantMessagesToggleValue>(
    () => {
      return (
        toggleValueParam ??
        (localStorage.getItem(key) as ImportantMessagesToggleValue | null) ??
        defaultValue
      )
    }
  )

  useEffect(() => {
    localStorage.setItem(key, toggleValue)
    setSearchParams({ toggle: toggleValue })
  }, [toggleValue, key, setSearchParams])

  return [toggleValue, setToggleValue] as const
}

// Categorize messages based on date
const categorizeMessages = (
  messages: ImportantMessage[] = []
): CategorizedMessages => {
  const now = new Date()
  const activeMessages = messages.filter(
    (message) =>
      new Date(message.startDate) <= now && new Date(message.endDate) >= now
  )
  const expiredMessages = messages.filter(
    (message) => new Date(message.endDate) < now
  )
  return {
    activeMessages,
    expiredMessages,
    allMessages: messages
  }
}

type ModalState = {
  type: 'create' | 'edit' | 'delete' | null
  message?: ImportantMessage
}

export interface CategorizedMessages {
  activeMessages: ImportantMessage[]
  expiredMessages: ImportantMessage[]
  allMessages: ImportantMessage[]
}

export function ImportantMessagesScreen() {
  const [modalState, setModalState] = useState<ModalState>({
    type: null,
    message: undefined
  })
  const openCreateModal = () => setModalState({ type: 'create' })
  const openEditModal = (message: ImportantMessage) =>
    setModalState({ type: 'edit', message })
  const openDeleteModal = (message: ImportantMessage) =>
    setModalState({ type: 'delete', message })
  const closeModal = () => setModalState({ type: null, message: undefined })

  const {
    data: importantMessages,
    isPending,
    isError,
    isSuccess
  } = useGetImportantMessagesQuery()
  const deleteImportantMessage = useDeleteImportantMessageMutation()
  const showError = useErrorToast('slette meldingen')
  const [toggleValue, setToggleValue] = useToggleValue(
    'localSettings.importantMessages.toggleValue',
    'all'
  )

  const categorizedMessages = useMemo(
    () => categorizeMessages(importantMessages),
    [importantMessages]
  )
  const filteredMessages = useMemo(() => {
    switch (toggleValue) {
      case 'active':
        return categorizedMessages.activeMessages
      case 'expired':
        return categorizedMessages.expiredMessages
      default:
        return categorizedMessages.allMessages
    }
  }, [toggleValue, categorizedMessages])

  const handleDelete = () => {
    if (modalState.message) {
      deleteImportantMessage
        .mutateAsync({
          messageId: modalState.message.id,
          etag: modalState.message.etag
        })
        .then(() => {
          Mtds.toast.success('Meldingen er slettet')
          closeModal()
        })
        .catch((error) => showError(error))
    }
  }

  return (
    <AppLayout
      useMobileBar={false}
      sidebar={<SideBarLayout />}
      topBar={
        <TopBar
          logo={<AppLogo />}
          title='Viktige meldinger'
          showNotificationBell={true}
        />
      }
      isLoading={isPending}
      isError={isError}
    >
      {isSuccess && (
        <>
          <ScreenHeader
            title='Viktige meldinger'
            description={
              <p>
                Her finner du meldinger som vises til brukere i{' '}
                <a href={PROVETA_URL}>Prøveta</a>. Opprett nye meldinger, endre
                publiseringsperioden og følg med på hvilke meldinger som er
                aktive eller utløpt.
              </p>
            }
            actions={
              <Mtds.Button data-variant='primary' onClick={openCreateModal}>
                <PlusIcon />
                Ny melding
              </Mtds.Button>
            }
          />
          <Mtds.Flex data-justify='space-between'>
            <ImportantMessagesScreenToggleGroup
              toggleValue={toggleValue}
              setToggleValue={setToggleValue}
              categorizedMessages={categorizedMessages}
            />
          </Mtds.Flex>
          <Mtds.Grid>
            {!filteredMessages.length && (
              <EmptyState
                title='Ingen meldinger'
                description='Opprett en melding for å komme i gang.'
              />
            )}
            {filteredMessages.map((message) => (
              <ImportantMessageCard
                key={message.id}
                message={message}
                onClickEdit={(message) => openEditModal(message)}
                onClickDelete={(message) => openDeleteModal(message)}
              />
            ))}
          </Mtds.Grid>
        </>
      )}
      {modalState.type === 'create' && (
        <CreateImportantMessageModal isOpen={true} setOpen={closeModal} />
      )}
      {modalState.type === 'edit' && modalState.message != null && (
        <EditImportantMessageModal
          isOpen={true}
          setOpen={closeModal}
          message={modalState.message}
        />
      )}
      {modalState.type === 'delete' && (
        <ConfirmDeletionModal
          title={`Meldingen «${modalState.message?.title ?? ''}» vil bli permanent slettet.`}
          isOpen={true}
          setOpen={closeModal}
          isPending={deleteImportantMessage.isPending}
          onConfirm={handleDelete}
        />
      )}
    </AppLayout>
  )
}
